import type { AnnouncementCreateForm } from '@app/constants/StoreTypes'

import type { AnnouncementCreateRequest, DirectAnnouncementRequest, TrustedOrderRequest } from '@app/packages/api/v2/types/params'

import { defineAll } from './defineAll'

/**
 * `preserved` carries server-owned fields the form cannot express. They must be echoed back on PUT,
 * otherwise editing clears them. Deliberately not part of FormState — that is persisted to
 * localStorage and reused to seed the next new announcement.
 */
export function convertAnnouncementFormToRequest(
  form: Extract<AnnouncementCreateForm, { type: 'basic' }>,
  preserved: { auto_accept?: boolean } = {}
): AnnouncementCreateRequest {
  return defineAll<AnnouncementCreateRequest>({
    start_date: form.start_date,
    end_date: form.end_date,
    start: form.start,
    end: form.end,
    children: form.children,
    address: form.location.address,
    latitude: form.location.latitude,
    longitude: form.location.longitude,
    school: form.school,
    special_needs: form.special_needs,
    description: form.description,
    price: form.price?.finalValue || null,
    currency: form.price?.currency,
    price_policy: form.price?.floatValue ? form.price_policy : 'flexible',
    regular: form.regular ?? false,
    sick_child: form.sick_child ?? false,
    auto_accept: preserved.auto_accept ?? false,
    online: form.online,
    allow_date_jump: form.allow_date_jump,
    external_publication_consent: form.external_publication_consent ?? false,
  })
}

export function convertTrustedAnnouncementFormToRequest(form: Extract<AnnouncementCreateForm, { type: 'trusted' }>): TrustedOrderRequest {
  return defineAll<TrustedOrderRequest>({
    start_date: form.start_date,
    end_date: form.end_date,
    start: form.start,
    end: form.end,
    children: form.children,
    address: form.location.address,
    latitude: form.location.latitude,
    longitude: form.location.longitude,
    description: form.description,
    allow_date_jump: !!form.allow_date_jump,
    online: !!form.online,
    currency: form.price.currency,
    price: form.price.finalValue,
  })
}

export function convertDirectAnnouncementFormToRequest(sitterId: string, form: Extract<AnnouncementCreateForm, { type: 'direct' }>): DirectAnnouncementRequest {
  return defineAll<DirectAnnouncementRequest>({
    user_id: sitterId,
    allow_date_jump: !!form.allow_date_jump,
    online: !!form.online,
    children: form.children,
    start_date: form.start_date,
    end_date: form.end_date,
    description: form.description,
    end: form.end,
    price: form.price.finalValue,
    currency: form.price.currency,
    special_needs: !!form.special_needs,
    sick_child: !!form.sick_child,
    start: form.start,
    address: form.location.address,
    latitude: form.location.latitude,
    longitude: form.location.longitude,
  })
}
